import React from "react";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useMyEntitlements } from "@seabrookstudios/pitch2table";
import { Features } from "../games/features";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Upgrade from "@mui/icons-material/Upgrade";
import { Path } from "../Path";
import { Link, useNavigate } from "react-router-dom";
import { SettingsCategory } from "../settings/Category";
import Button from "@mui/material/Button";

/**
 * @param {object} props
 * @param {import("react").ReactNode} props.children
 * @param {keyof import("@seabrookstudios/pitch2table-core").UserEntitlements} props.entitlement
 * @returns
 */
export const HasEntitlement = ({ children, entitlement }) => {
  const { data: entitlements } = useMyEntitlements();

  if (!entitlements) {
    return null;
  }

  if (!entitlements[entitlement]) {
    return null;
  }

  return children;
};

/**
 * @param {object} props
 * @param {import("react").ReactNode} props.children
 * @param {import("react").ReactNode} props.LockedView
 * @returns
 */
export const IsMember = ({ children, LockedView }) => {
  const { data: entitlements } = useMyEntitlements();

  if (!entitlements) {
    return LockedView;
  }

  if (entitlements.isMember) {
    return children;
  }

  if (!Features.PremiumMode) {
    return null;
  }

  return LockedView;
};

/**
 * @param {object} props
 * @param {string} props.title
 */
export const LockedListItem = ({ title }) => {
  return (
    <ListItem>
      <ListItemText primary={title} secondary="Upgrade to premium to access" />
      <ListItemIcon>
        <IconButton component={Link} to={Path.Premium} color="primary">
          <WorkspacePremiumIcon />
        </IconButton>
      </ListItemIcon>
    </ListItem>
  );
};

/**
 * Present a button that is only unlocked for premium users.
 * @param {object} props
 * @param {import("react").ReactNode} props.children
 * @param {string} props.lockedLabel
 * @param {"contained" | "outlined"} props.variant
 */
export const LockedButton = ({ children, lockedLabel, variant = "contained" }) => {
  const { data: entitlements } = useMyEntitlements();

  if (!entitlements) {
    return null;
  }

  if (entitlements.isMember) {
    return children;
  }

  return (
    <Button variant={variant} color="primary" component={Link} to={Path.Premium} endIcon={<WorkspacePremiumIcon />}>
      {lockedLabel}
    </Button>
  );
};

/**
 * @param {object} props
 * @param {string} props.title
 */
export const LockedBanner = ({ title }) => {
  return (
    <Box flex={1}>
      <Alert
        severity="info"
        icon={<WorkspacePremiumIcon />}
        action={
          <IconButton component={Link} to={Path.Premium} color="info">
            <Upgrade color="info" />
          </IconButton>
        }
      >
        {title}
      </Alert>
    </Box>
  );
};

export const LockedListItemText = ({ primary, secondary }) => {
  return (
    <ListItem>
      <ListItemText primary={primary} secondary={secondary} />
      <ListItemIcon>
        <IconButton component={Link} to={Path.Premium} color="primary">
          <WorkspacePremiumIcon />
        </IconButton>
      </ListItemIcon>
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {string} props.category
 * @param {string} props.helperText
 * @param {import("@mui/material/OverridableComponent").OverridableComponent<import("@mui/material").SvgIconTypeMap<{}, "svg">>} props.Icon
 */
export const LockedCategory = ({ category, helperText, Icon }) => {
  const navigate = useNavigate();

  const onClickHandler = () => {
    navigate(Path.Premium);
  };

  return (
    <SettingsCategory
      category={category}
      helperText={helperText}
      selected={false}
      onClick={onClickHandler}
      Icon={Icon}
      badgeCount={0}
      id={category}
      right={
        <IconButton component={Link} to={Path.Premium} color="primary">
          <WorkspacePremiumIcon />
        </IconButton>
      }
    />
  );
};
